import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Calendar, type Range, type FormatDateString, type DateMode } from 'vanilla-calendar-pro';
import { computed, ref, watch, toRefs } from 'vue';
import { DateRangeInputValue, isDateRangeValue } from '../types';

dayjs.extend(customParseFormat);

type ModelValue = string | string[] | DateRangeInputValue;

type Props = {
  modelValue: ModelValue;
  dateMin?: string;
  dateMax?: string;
  readonly?: boolean;
  disabled?: boolean;
  isHiddenAfterSelect?: boolean;
  multiple?: boolean;
};

type Payload = {
  props: Props;
  range?: boolean;
  emit: (event: 'update:model-value', payload: ModelValue) => void;
};

export const FORMAT_DATE_RU = 'DD.MM.YYYY';
export const FORMAT_DATE_VC = 'YYYY-MM-DD';
export const RegexFormatDateRu = /^\d{2}.\d{2}.\d{4}$/;

export function validateDateRu(dateString: string) {
  if (!RegexFormatDateRu.test(dateString)) {
    return false;
  }

  return dayjs(dateString, FORMAT_DATE_RU, true).isValid();
}

const locale = {
  months: {
    short: ['янв.', 'февр.', 'март', 'апр.', 'май', 'июнь', 'июль', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
    long: [
      'январь',
      'февраль',
      'март',
      'апрель',
      'май',
      'июнь',
      'июль',
      'август',
      'сентябрь',
      'октябрь',
      'ноябрь',
      'декабрь',
    ],
  },
  weekdays: {
    short: ['В', 'П', 'В', 'С', 'Ч', 'П', 'С'],
    long: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
  },
};

const layouts = {
  default: `
      <div class="vc-header" data-vc="header" role="toolbar" aria-label="Calendar Navigation">
        <#ArrowPrev />
        <div class="vc-header__content" data-vc-header="content">
          <#Month />
          <#Year />
        </div>
        <#ArrowNext />
      </div>
      <div class="vc-wrapper" data-vc="wrapper">
        <#WeekNumbers />
        <div class="vc-content" data-vc="content">
          <#Week />
          <#Dates />
          <#DateRangeTooltip />
        </div>
      </div>
      <#ControlTime />
      <button id="btn-clear" class="vc-clear" type="button">Очистить</button>
    `,
};

export const useDatePicker = ({ props, emit, range = false }: Payload) => {
  const opened = ref(false);
  const inputValue = ref('');
  const datePicker = ref<Calendar>();

  const { modelValue } = toRefs(props);

  const isMultiple = computed(() => props.multiple || false);

  const checkDateValidity = (dateString: string): boolean => {
    if (!validateDateRu(dateString)) {
      return false;
    }

    const { dateMin, dateMax } = props;
    const date = dayjs(dateString, FORMAT_DATE_RU);

    if (dateMin && date.isBefore(dateMin, 'day')) {
      return false;
    }
    if (dateMax && date.isAfter(dateMax, 'day')) {
      return false;
    }

    return true;
  };

  const formatRuToDateString = (dateString: string) => {
    return dayjs(dateString, FORMAT_DATE_RU).format(FORMAT_DATE_VC) as FormatDateString;
  };

  const selectedDates = computed(() => {
    if (!inputValue.value) {
      return [];
    }

    if (isMultiple.value) {
      return inputValue.value.split(', ').map((date: string) => formatRuToDateString(date));
    }

    return range
      ? [inputValue.value.split(':').map(formatRuToDateString).join(':')]
      : [formatRuToDateString(inputValue.value)];
  });

  const selectedMonth = computed(() => {
    if (!inputValue.value) {
      return new Date().getMonth() as Range<12>;
    }

    return (
      range ? Number(inputValue.value.split(':')[0].split('.')[1]) - 1 : Number(inputValue.value.split('.')[1]) - 1
    ) as Range<12>;
  });

  const selectedYear = computed(() => {
    if (!inputValue.value) {
      return new Date().getFullYear();
    }

    return range ? Number(inputValue.value.split(':')[0].split('.')[2]) : Number(inputValue.value.split('.')[2]);
  });

  const selectSingleDateHandler = (self: Calendar) => {
    if (isMultiple.value && self.context.selectedDates.length) {
      self.context.selectedDates.sort((a, b) => +new Date(a) - +new Date(b));
      const value = self.context.selectedDates.filter(Boolean).map((date) => dayjs(date).toISOString());
      onInput(value);
    } else if (self.context.selectedDates[0]) {
      const value = dayjs(self.context.selectedDates[0]).toISOString();

      onInput(value);
    } else {
      inputValue.value = '';
    }

    if (props.isHiddenAfterSelect) {
      self.hide();
    }
  };

  const selectRangeDateHandler = (self: Calendar) => {
    if (self.context.selectedDates[1]) {
      self.context.selectedDates.sort((a, b) => +new Date(a) - +new Date(b));

      const value = {
        start: self.context.selectedDates[0],
        end: self.context.selectedDates[self.context.selectedDates.length - 1],
      };

      inputValue.value = `${dayjs(value.start).startOf('day').format(FORMAT_DATE_RU)}:${dayjs(value.end)
        .endOf('day')
        .format(FORMAT_DATE_RU)}`;

      if (props.isHiddenAfterSelect) {
        self.hide();
      }
    } else if (self.context.selectedDates[0]) {
      inputValue.value = `${dayjs(self.context.selectedDates[0]).startOf('day').format(FORMAT_DATE_RU)}:`;
    } else {
      inputValue.value = '';

      if (props.isHiddenAfterSelect) {
        self.hide();
      }
    }
  };

  const updateDatePicker = () => {
    if (!datePicker.value) {
      return;
    }

    datePicker.value.selectedDates = selectedDates.value;
    datePicker.value.selectedMonth = selectedMonth.value;
    datePicker.value.selectedYear = selectedYear.value;

    datePicker.value.update({
      year: true,
      month: true,
      dates: true,
      time: true,
    });
  };

  const showDatePicker = () => {
    if (props.disabled) {
      return;
    }

    datePicker.value.show();
  };

  const hideDatePicker = () => {
    if (props.disabled) {
      return;
    }

    datePicker.value.hide();
  };

  function clearDatePicker() {
    let value: ModelValue;

    if (isMultiple.value) {
      value = [];
    } else {
      value = range ? { start: '', end: '' } : '';
    }

    onInput(value);

    hideDatePicker();
    setTimeout(updateDatePicker, 0);
  }

  const datePickerSettings = computed(() => {
    const { dateMin, dateMax } = props;

    let selectionDatesMode: DateMode;

    if (isMultiple.value) {
      selectionDatesMode = 'multiple';
    } else {
      selectionDatesMode = range ? 'multiple-ranged' : 'single';
    }

    return {
      locale,
      layouts,
      inputMode: false,
      selectionDatesMode,
      selectedWeekends: [],
      selectedTheme: 'light',
      selectedDates: selectedDates.value,
      selectedMonth: selectedMonth.value,
      selectedYear: selectedYear.value,
      enableDateToggle: isMultiple.value,
      dateMin: dateMin ? (dayjs(dateMin).format(FORMAT_DATE_VC) as FormatDateString) : null,
      dateMax: dateMax ? (dayjs(dateMax).format(FORMAT_DATE_VC) as FormatDateString) : null,
      onClickDate(self: Calendar) {
        range ? selectRangeDateHandler(self) : selectSingleDateHandler(self);
      },
      onShow(self: Calendar) {
        opened.value = true;

        const btnClear = self.context.mainElement.querySelector<HTMLElement>('#btn-clear');

        if (!btnClear) {
          return;
        }

        const hasValue =
          (modelValue.value &&
            typeof modelValue.value === 'object' &&
            isDateRangeValue(modelValue.value) &&
            modelValue.value.start &&
            modelValue.value.end) ||
          (modelValue.value && typeof modelValue.value === 'string') ||
          (modelValue.value && Array.isArray(modelValue.value) && modelValue.value.length);

        if (hasValue) {
          btnClear.style.display = 'block';
        }

        btnClear.addEventListener('click', clearDatePicker, false);
      },
      onHide(self: Calendar) {
        opened.value = false;

        const btnClear = self.context.mainElement.querySelector<HTMLElement>('#btn-clear');

        if (btnClear) {
          btnClear.style.display = 'none';
          btnClear.removeEventListener('click', clearDatePicker);
        }
      },
    };
  });

  const initDatePicker = (element: HTMLElement | null) => {
    if (element) {
      datePicker.value = new Calendar(element, datePickerSettings.value);

      datePicker.value.init();
    }
  };

  const destroyDatePicker = () => {
    datePicker.value.destroy();
  };

  const onInput = (value: ModelValue) => {
    if (!props.disabled && !props.readonly) {
      emit('update:model-value', value);
    }
  };

  watch(inputValue, (value, oldValue) => {
    if (value === undefined || value === oldValue) {
      return;
    }

    if (value === '') {
      updateDatePicker();

      return;
    }

    if (isMultiple.value) {
      const isValid = value
        .split(', ')
        .map((date) => validateDateRu(date))
        .every(Boolean);

      if (isValid) {
        const dates = [...new Set(value.split(', '))]
          .filter((date) => validateDateRu && checkDateValidity(date))
          .map((date) => dayjs(date, FORMAT_DATE_RU).toISOString());

        onInput(dates);
      }
    } else {
      const mask = range ? `${FORMAT_DATE_RU}:${FORMAT_DATE_RU}` : FORMAT_DATE_RU;

      if (value.length === mask.length) {
        const isValid = range
          ? value
              .split(':')
              .map((dateFromRange) => validateDateRu(dateFromRange))
              .every(Boolean)
          : validateDateRu(value);

        if (isValid) {
          if (range) {
            const [start, end] = value.split(':');

            onInput({
              start: dayjs(start, FORMAT_DATE_RU).toISOString(),
              end: dayjs(end, FORMAT_DATE_RU).endOf('day').toISOString(),
            });

            return;
          }

          const date = dayjs(value, FORMAT_DATE_RU).toISOString();

          onInput(date);
        }
      }
    }
  });

  watch(
    modelValue,
    (value) => {
      if (value === undefined) {
        return;
      }

      if (value && Array.isArray(value)) {
        inputValue.value = value.map((date) => dayjs(date).format(FORMAT_DATE_RU)).join(', ');
      } else if (value && typeof value === 'object' && isDateRangeValue(value) && value.start && value.end) {
        const start = dayjs(value.start).isValid() ? dayjs(value.start).format(FORMAT_DATE_RU) : '';
        const end = dayjs(value.end).isValid() ? dayjs(value.end).format(FORMAT_DATE_RU) : '';

        inputValue.value = `${start}:${end}`;
      } else if (value && typeof value === 'string' && dayjs(value).isValid()) {
        inputValue.value = dayjs(value).format(FORMAT_DATE_RU);
      } else {
        inputValue.value = '';
      }
    },
    { immediate: true, deep: true },
  );

  return {
    opened,
    inputValue,

    initDatePicker,
    showDatePicker,
    hideDatePicker,
    updateDatePicker,
    destroyDatePicker,
    checkDateValidity,
  };
};
